import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import type {
  DiscountContractMappingDto,
  DiscountContractMappingRequestDto,
  ResponseDtoPageDiscountContractMappingDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

export type { DiscountContractMappingDto };

export interface DiscountDto {
  id?: string;
  services?: string;
  revenueRangeMin?: number;
  revenueRangeMax?: number | null;
  discount?: number;
  routeStartWith?: string;
}

export interface DiscountListResponse {
  data?: DiscountDto[];
  totalItems?: number;
}

export interface DiscountFilters {
  contractIds?: string[];
  services?: string[];
}

const mapToDiscountDto = (item: DiscountContractMappingDto): DiscountDto => ({
  id: item.id,
  services: item.service,
  revenueRangeMin: item.minimumAmountSpent,
  revenueRangeMax: item.maximumAmountSpent ?? null,
  discount: item.discountRate,
  routeStartWith: item.routeStartWith,
});

export const useDiscountList = (
  filters?: DiscountFilters,
  pagination?: { page: number; size: number },
  options?: { enabled?: boolean }
): UseQueryResult<DiscountListResponse> =>
  useQuery(
    ['discounts', filters, pagination],
    async (): Promise<DiscountListResponse> => {
      const requestDto: DiscountContractMappingRequestDto = {
        paginationRequest: {
          page: pagination?.page ?? 0,
          size: pagination?.size ?? 20,
        },
        ...(filters?.contractIds?.length ? { contractIds: filters.contractIds } : {}),
        ...(filters?.services?.length ? { services: filters.services } : {}),
      };

      const response = await tFinancialService.api.queryDiscountContractMappings(requestDto);
      const pageData = (response.data as ResponseDtoPageDiscountContractMappingDto).data;

      return {
        data: (pageData?.content ?? []).map(mapToDiscountDto),
        totalItems: pageData?.totalElements ?? 0,
      };
    },
    {
      keepPreviousData: true,
      enabled: options?.enabled !== false,
    }
  );

export const useUpsertDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation(async (data: DiscountDto): Promise<DiscountDto> => data, {
    onSuccess: () => {
      queryClient.invalidateQueries(['discounts']);
    },
  });
};
